import { faBed, faDoorOpen, faMoon, faUserGroup, faUserPlus, faPlus, faTrash } from '@fortawesome/pro-regular-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { IconButton, Typography } from '@mui/material'
import { GridColDef } from '@mui/x-data-grid'
import { Trans, useTranslation } from 'react-i18next'
import { MenuItem, Select, Stack, Tooltip } from '@vapor/material'
import { FormNumericField, LocalizedString } from 'hic-components'
import { range } from 'src/utils/utils'
import ResettableTextField from '../../../reservation-insert/components/resettable-text-field'
import { rtUnavailableMagicNumber } from '../../../reservation-insert/components/accordion/reservation-accordion'
import {
  ReservationGroupUpsertGridRowGroupingInfo,
  ReservationGroupUpsertGridRowOccupancy,
  isReservationGroupUpsertGridTotalRow,
  ReservationGroupUpsertGridRow,
  ReservationGroupUpsertGridTotalRow,
  ReservationRatesPerOccupancy,
} from '../reservation-group.types'
import { useRateQueryForResourceLazyQuery } from 'src/api/api.generated'
import useErrorSnackBar from 'src/app/hooks/error-snack-bar/error-snack-bar'
import useLocalizeString from 'src/app/hooks/current-locale/use-localized-string'
import ReservationGroupUpsertAveragePriceField from './reservation-group-upsert-average-price-field'
import { useCallback } from 'react'

export default function useReservationGroupUpsertGridColumn(
  hideChildren: boolean,
  childrenAgeRangesLabels: LocalizedString[],
  reservationRates: ReservationRatesPerOccupancy,
  updateRowResourcesNumber: (groupingInfo: ReservationGroupUpsertGridRowGroupingInfo, rowId: string, selectedResourcesNumber: number) => void,
  updateRowOccupancy: (groupingInfo: ReservationGroupUpsertGridRowGroupingInfo, rowId: string, newOccupancy: ReservationGroupUpsertGridRowOccupancy, newPrice: number) => void,
  updateRowTotalPrice: (groupingInfo: ReservationGroupUpsertGridRowGroupingInfo, rowId: string, newPrice: number) => void,
  updateRowInitialPrice: (groupingInfo: ReservationGroupUpsertGridRowGroupingInfo, rowId: string, newPrice: number) => void,
  createRow: (groupingInfo: ReservationGroupUpsertGridRowGroupingInfo) => void,
  removeRow: (groupingInfo: ReservationGroupUpsertGridRowGroupingInfo, rowId: string) => void,
  updateTotalRowTotalPrice: (initialPrice: number, newPrice: number) => void,
) {
  const { t } = useTranslation()
  const showError = useErrorSnackBar()
  const localizeString = useLocalizeString()

  const [reservationRatesQuery, { loading }] = useRateQueryForResourceLazyQuery()
  const calculateAveragePrice = useCallback(
    async (row: ReservationGroupUpsertGridRow) => {
      // optimization to avoid calling the query if the adults price is already received from the server
      if (reservationRates && (row.occupancies.children.length === 0 || row.occupancies.children.every((c) => c.quantity === 0))) {
        let resourceRate = null
        for (const rate of reservationRates[row.occupancies.adults]?.rateQuery?.rates ?? []) {
          for (const resourceRateToCheck of rate?.resourceRates ?? []) {
            if (resourceRateToCheck.resourceTypeId === row.filters.resourceTypeId) {
              resourceRate = resourceRateToCheck
              break
            }
          }
          if (resourceRate) break
        }

        if (resourceRate) {
          return Math.round(resourceRate.dailyPrice.reduce((prev, curr) => prev + curr.price, 0) / row.nights)
        }
      }

      const result = await reservationRatesQuery({
        variables: {
          filter: {
            ...row.filters,
            adults: row.occupancies.adults,
            childOccupancy: row.occupancies.children.map((child) => {
              return {
                childId: child.childId,
                quantity: child.quantity,
              }
            }),
          },
        },
      })
      if (!result.error && result.data) {
        return Math.round(
          (result.data.rateQueryForResource.rate.resourceRates?.map((rr) => rr.dailyPrice.reduce((prev, curr) => prev + curr.price, 0)).reduce((prev, curr) => prev + curr, 0) ?? 0) / row.nights,
        )
      } else if (result.error) {
        showError(result.error)
      }
    },
    [reservationRatesQuery, showError, reservationRates],
  )

  const columns: GridColDef[] = [
    {
      field: 'type',
      headerName: t('pms.booking.reservation_group_new.grid.header_typology'),
      disableColumnMenu: true,
      sortable: false,
      flex: 1,
      minWidth: 150,
      renderHeader: () => {
        return (
          <Typography variant="body" noWrap style={{ width: 'calc(100%)' }}>
            <b>{t('pms.booking.reservation_group_new.grid.header_typology')}</b>
          </Typography>
        )
      },
      renderCell: (params) => {
        const isTotalRow = isReservationGroupUpsertGridTotalRow(params.row)
        if (isTotalRow) {
          return (
            <Typography variant="body" align="center" width="100%">
              <b>{t('pms.booking.reservation_group_new.grid.total_row.total')}</b>
            </Typography>
          )
        } else {
          const { type, indexInTheGroup } = params.row as ReservationGroupUpsertGridRow
          return (
            <>
              {indexInTheGroup === 0 && (
                <Stack direction="column" width="calc(100%)">
                  <Typography variant="body" noWrap style={{ width: 'calc(100%)' }}>
                    <b>{type.resourceTypeName}</b>
                  </Typography>
                  <Stack direction="row" justifyContent="flex-start" alignItems="center" spacing={0.5}>
                    <FontAwesomeIcon icon={faUserGroup} />
                    <Typography variant="body" noWrap style={{ width: 'calc(100%)' }}>
                      <Trans i18nKey="pms.booking.reservation_group_new.grid.occupancy" values={{ occupancy: type.occupancy, maxOccupancy: type.maxOccupancy }} />
                    </Typography>
                  </Stack>
                  <Stack direction="row" justifyContent="flex-start" alignItems="center" spacing={0.5}>
                    <FontAwesomeIcon icon={faDoorOpen} color={type.notAvailable ? 'red' : undefined} />
                    <Typography variant="body" color={type.notAvailable ? 'red' : undefined} noWrap style={{ width: 'calc(100%)' }}>
                      {type.availability !== rtUnavailableMagicNumber && (
                        <Trans i18nKey="pms.booking.reservation_group_new.grid.availability" values={{ availability: type.availability, maxAvailability: type.maxAvailability }} />
                      )}
                      {type.availability === rtUnavailableMagicNumber && t('commons.not_available')}
                    </Typography>
                  </Stack>
                </Stack>
              )}
            </>
          )
        }
      },
      headerAlign: 'center',
    },
    {
      field: 'accomodations',
      headerName: t('pms.booking.reservation_group_new.grid.header_resources'),
      disableColumnMenu: true,
      sortable: false,
      minWidth: 130,
      renderCell: (params) => {
        const isTotalRow = isReservationGroupUpsertGridTotalRow(params.row)

        if (isTotalRow) {
          const row = params.row as ReservationGroupUpsertGridTotalRow
          return (
            <Stack direction="column" justifyContent="center" alignItems="flex-start" spacing={1}>
              <Stack direction="row" justifyContent="center" alignItems="center" spacing={0.5}>
                <FontAwesomeIcon icon={faBed} />
                <Typography variant="body">{t('pms.booking.reservation_group_new.grid.total_row.accomodations', { count: row.resources.resourceNumber })}</Typography>
              </Stack>
              <Stack direction="row" justifyContent="center" alignItems="center" spacing={0.5}>
                <FontAwesomeIcon icon={faUserGroup} />
                <Typography variant="body">
                  <Trans i18nKey="pms.booking.reservation_group_new.grid.total_row.guest_number" values={{ base: row.resources.occupancy, max: row.resources.maxOccupancy }} />
                </Typography>
              </Stack>
            </Stack>
          )
        } else {
          const row = params.row as ReservationGroupUpsertGridRow
          return <FormNumericField disabled={loading} size="small" value={row.resourceNumber} onChange={(value) => updateRowResourcesNumber(row.groupingInfo, row.id, value ?? 0)} />
        }
      },
      renderHeader: () => {
        return (
          <Stack direction="column" spacing={0.5} justifyContent="center" alignItems="center" width="calc(100%)">
            <FontAwesomeIcon icon={faBed} />
            <Typography
              variant="body"
              align="center"
              sx={{
                whiteSpace: 'initial',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                display: '-webkit-box',
                WebkitLineClamp: '2',
                WebkitBoxOrient: 'vertical',
              }}
            >
              <b>{t('pms.booking.reservation_group_new.grid.header_resources')}</b>
            </Typography>
          </Stack>
        )
      },
    },
    {
      field: 'occupancies',
      headerName: t('pms.booking.reservation_group_new.grid.header_guests_per_accomodation'),
      disableColumnMenu: true,
      sortable: false,
      hideable: true,
      width: hideChildren ? 90 : 245,
      renderCell: (params) => {
        const isTotalRow = isReservationGroupUpsertGridTotalRow(params.row)

        if (isTotalRow) {
          const row = params.row as ReservationGroupUpsertGridTotalRow
          return (
            <Stack direction="column" justifyContent="center" alignItems="flex-start" spacing={-0.3}>
              <Typography variant="body">
                <Trans i18nKey="pms.booking.reservation_group_new.grid.total_row.adults" values={{ count: row.occupancy.adults }} />
              </Typography>
              {row.occupancy.children.map((child) => {
                return (
                  <Typography key={child.description} variant="body">
                    {`${child.description}: `}
                    <b>{child.quantity}</b>
                  </Typography>
                )
              })}
            </Stack>
          )
        } else {
          const row = params.row as ReservationGroupUpsertGridRow
          return (
            <Stack direction="row" justifyContent="center" alignItems="center" spacing={0.5} width="100%">
              <Select
                disabled={loading}
                sx={{ width: '55px' }}
                value={row.occupancies.adults}
                onChange={async (value) => {
                  const newPrice = await calculateAveragePrice({ ...row, occupancies: { ...row.occupancies, adults: value.target.value as number } })
                  if (newPrice !== undefined) {
                    updateRowOccupancy(row.groupingInfo, row.id, { ...row.occupancies, adults: value.target.value as number }, newPrice)
                  }
                }}
                error={row.type.occupancyError}
                size="small"
              >
                {range(0, 10).map((val) => {
                  return (
                    <MenuItem key={val} value={val}>
                      {val}
                    </MenuItem>
                  )
                })}
              </Select>

              {row.occupancies.hideChildren === false && (
                <>
                  {row.occupancies.children.map((child) => {
                    return (
                      <Select
                        key={child.childId}
                        disabled={loading}
                        size="small"
                        sx={{ width: '55px' }}
                        value={child.quantity}
                        onChange={async (value) => {
                          const updatedChildren = row.occupancies.children.map((c) => {
                            if (c.childId === child.childId) return { ...c, quantity: value.target.value as number }
                            return c
                          })
                          const newPrice = await calculateAveragePrice({ ...row, occupancies: { ...row.occupancies, children: updatedChildren } })
                          if (newPrice !== undefined) {
                            updateRowOccupancy(
                              row.groupingInfo,
                              row.id,
                              {
                                ...row.occupancies,
                                children: updatedChildren,
                              },
                              newPrice,
                            )
                          }
                        }}
                        error={row.type.occupancyError && !child.noOccupancy}
                      >
                        {range(0, 10).map((val) => {
                          return (
                            <MenuItem key={val} value={val}>
                              {val}
                            </MenuItem>
                          )
                        })}
                      </Select>
                    )
                  })}
                </>
              )}
            </Stack>
          )
        }
      },
      renderHeader: () => {
        return (
          <Stack direction="column" spacing={0.5} justifyContent="center" alignItems="center" width="calc(100%)">
            <FontAwesomeIcon icon={faUserGroup} />
            <Stack direction="column" spacing={0}>
              <Typography
                variant="body"
                align="center"
                sx={{
                  whiteSpace: 'initial',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  display: '-webkit-box',
                  WebkitLineClamp: '2',
                  WebkitBoxOrient: 'vertical',
                }}
              >
                <b>{t('pms.booking.reservation_group_new.grid.header_guests_per_accomodation')}</b>
              </Typography>

              {!hideChildren && (
                <Stack direction="row" spacing={1}>
                  <Typography variant="body" align="center">
                    <b>{t('pms.booking.reservation_group_new.grid.occupancy_adults')}</b>
                  </Typography>
                  {[...childrenAgeRangesLabels].reverse().map((c, index) => (
                    <Typography key={index} variant="body" align="center">
                      <b>{localizeString(c)}</b>
                    </Typography>
                  ))}
                </Stack>
              )}
            </Stack>
          </Stack>
        )
      },
      headerAlign: 'center',
    },
    {
      field: 'avgPrice',
      headerName: t('pms.booking.reservation_group_new.grid.header_avg_price'),
      disableColumnMenu: true,
      sortable: false,
      minWidth: 170,
      renderHeader: () => {
        return (
          <Stack direction="column" spacing={0.5} justifyContent="center" alignItems="center" width="calc(100%)">
            <FontAwesomeIcon icon={faMoon} />
            <Typography
              variant="body"
              align="center"
              sx={{
                whiteSpace: 'initial',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                display: '-webkit-box',
                WebkitLineClamp: '2',
                WebkitBoxOrient: 'vertical',
              }}
            >
              <b>{t('pms.booking.reservation_group_new.grid.header_avg_price')}</b>
            </Typography>
          </Stack>
        )
      },
      renderCell: (params) => {
        return <ReservationGroupUpsertAveragePriceField row={params.row} updateRowTotalPrice={updateRowTotalPrice} updateTotalRowTotalPrice={updateTotalRowTotalPrice} loading={loading} />
      },
    },
    {
      field: 'totalPrice',
      headerName: t('pms.booking.reservation_group_new.grid.header_total'),
      disableColumnMenu: true,
      minWidth: 170,
      sortable: false,
      headerAlign: 'center',
      renderHeader: () => {
        return (
          <Typography
            variant="body"
            align="center"
            sx={{
              whiteSpace: 'initial',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              display: '-webkit-box',
              WebkitLineClamp: '2',
              WebkitBoxOrient: 'vertical',
            }}
          >
            <b>{t('pms.booking.reservation_group_new.grid.header_total')}</b>
          </Typography>
        )
      },
      renderCell: (params) => {
        const isTotalRow = isReservationGroupUpsertGridTotalRow(params.row)

        if (isTotalRow) {
          const row = params.row as ReservationGroupUpsertGridTotalRow
          return (
            <Stack direction="row" justifyContent="flex-end" alignItems="center" width="100%">
              <ResettableTextField
                disabled={loading || row.disablePrices}
                changeOnBlur
                initialValue={row.initialTotalPrice}
                min={0}
                value={row.totalPrice}
                onChange={(value) => updateTotalRowTotalPrice(row.initialTotalPrice, value ?? 0)}
                resetPosition={'LEFT'}
              />
            </Stack>
          )
        } else {
          const row = params.row as ReservationGroupUpsertGridRow
          return (
            <Stack direction="row" justifyContent="flex-end" alignItems="center" width="100%">
              <ResettableTextField
                disabled={loading}
                changeOnBlur
                initialValue={row.initialTotalPrice}
                min={0}
                value={row.totalPrice}
                onChange={(value) => updateRowTotalPrice(row.groupingInfo, row.id, value ?? 0)}
                resetPosition={'LEFT'}
              />
            </Stack>
          )
        }
      },
    },
    {
      field: 'actions',
      headerName: '',
      disableColumnMenu: true,
      sortable: false,
      renderHeader: () => <FontAwesomeIcon icon={faUserPlus} />,
      headerAlign: 'center',
      width: 50,
      renderCell: (params) => {
        const isTotalRow = isReservationGroupUpsertGridTotalRow(params.row)

        if (isTotalRow) {
          return <></>
        } else {
          const row = params.row as ReservationGroupUpsertGridRow
          return (
            <Stack direction="row" justifyContent="center" alignItems="center" width="100%">
              {row.indexInTheGroup === 0 && (
                <Tooltip title={t('pms.booking.reservation_group_new.grid.add_accomodation')}>
                  <div>
                    <IconButton disabled={loading} onClick={() => createRow(row.groupingInfo)}>
                      <FontAwesomeIcon icon={faPlus} />
                    </IconButton>
                  </div>
                </Tooltip>
              )}
              {row.indexInTheGroup > 0 && (
                <Tooltip title={t('pms.booking.reservation_group_new.grid.remove_accomodation')}>
                  <div>
                    <IconButton disabled={loading} onClick={() => removeRow(row.groupingInfo, row.id)}>
                      <FontAwesomeIcon icon={faTrash} />
                    </IconButton>
                  </div>
                </Tooltip>
              )}
            </Stack>
          )
        }
      },
    },
  ]

  return { columns }
}
